import { Suspense, lazy } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { useAuth } from './auth/AuthContext';
import { RequireAuth } from './auth/RequireAuth';
import { PosterFrame } from './components/PosterFrame';
import { ListingForm } from './pages/ListingForm';
import { ListingBundles } from './pages/ListingBundles';
import { ListingPromote } from './pages/ListingPromote';
import { MySaleListings } from './pages/MySaleListings';
import { SaleListingForm } from './pages/SaleListingForm';
import { SaleListingPay } from './pages/SaleListingPay';
import { SaleVerification } from './pages/SaleVerification';
import { RequireAdmin } from './auth/RequireAdmin';
import { RequireTenant } from './auth/RequireTenant';
import { RequireOwner } from './auth/RequireOwner';
import { usePortal } from './lib/portal';
/* The Control Centre is twenty-two screens only an operator opens; it loads
   on demand instead of riding in the bundle every visitor downloads. */
const AdminRoutes = lazy(() => import('./routes/AdminRoutes'));
const PortalRoutes = lazy(() => import('./routes/PortalRoutes'));
const OwnerRoutes = lazy(() => import('./routes/OwnerRoutes'));
const AppRoutes = lazy(() => import('./routes/AppRoutes'));
import { Blog } from './pages/Blog';
import { ArticleDetail } from './pages/ArticleDetail';
import { ShareStory } from './pages/ShareStory';
import { Discover } from './pages/Discover';
import { Services } from './pages/Services';
import { ServiceProvider } from './pages/ServiceProvider';
import { ServiceRegister } from './pages/ServiceRegister';
import { Marketplace } from './pages/Marketplace';
import { MarketplaceItem } from './pages/MarketplaceItem';
import { MarketplacePost } from './pages/MarketplacePost';
import { PostSomething } from './pages/PostSomething';
import { ProviderSignup } from './pages/ProviderSignup';
import { MyListings } from './pages/MyListings';
import { ListingDetail } from './pages/ListingDetail';
import { BuyProperty } from './pages/BuyProperty';
import { SaleDetail } from './pages/SaleDetail';
import { SellProperty } from './pages/SellProperty';
import { Landing } from './pages/Landing';
import { NotFound } from './pages/NotFound';
import { Solutions } from './pages/Solutions';
import { About } from './pages/About';
import { RequestDemo } from './pages/RequestDemo';
import { RequestQuote } from './pages/RequestQuote';
import { Privacy } from './pages/Privacy';
import { Terms } from './pages/Terms';
import { DeleteAccount } from './pages/DeleteAccount';
import { Login } from './pages/Login';
import { TwoFactorSetup } from './pages/TwoFactorSetup';
import { Register } from './pages/Register';
import { ForgotPassword } from './pages/ForgotPassword';
import { ResetPassword } from './pages/ResetPassword';
import { AcceptInvitation } from './pages/AcceptInvitation';
import { LoggedOut } from './pages/LoggedOut';
import { InspectionConfirm } from './pages/InspectionConfirm';

export function App() {
  const { isAuthenticated } = useAuth();
  // Where THIS person's app lives. There are three answers now, so the two
  // "already signed in, go away" redirects below can no longer assume "/".
  const { appHome } = usePortal();

  return (
    <Routes>
      {/* The public landing page owns "/" for a visitor who is not signed in
          (amendment A1). Once signed in the same path is the dashboard, so a
          returning landlord never has to walk past marketing to reach work. */}
      {!isAuthenticated && <Route path="/" element={<Landing />} />}

      {/* The same homepage at a permanent address.
          "/" belongs to the dashboard once you sign in, which left the public
          homepage with no URL at all for the people most likely to want it —
          an operator in the Control Center, or a landlord sending a colleague
          to the site. This is that URL, in both auth states, and Landing marks
          "/" as its canonical so the pair is never read as two pages. */}
      <Route path="/home" element={<Landing />} />

      {/* Marketing pages are public in both auth states: a signed-in landlord
          can still read About or Solutions, or send a colleague to /demo. */}
      <Route path="/solutions" element={<Solutions />} />
      <Route path="/about" element={<About />} />
      {/* Find Your Next Home. Public, no login, its own top-level path. */}
      <Route path="/homes" element={<Discover />} />
      <Route path="/homes/:id" element={<ListingDetail />} />
      {/* Buying, beside renting and at the top level for the same SEO reason —
          never /explore/buy. /sell is the door a seller comes through. */}
      <Route path="/buy" element={<BuyProperty />} />
      <Route path="/buy/:id" element={<SaleDetail />} />
      <Route path="/sell" element={<SellProperty />} />
      {/* Explore · Services. /register sits above /:slug so it is not read as
          a business name. */}
      <Route path="/services" element={<Services />} />
      <Route path="/services/register" element={<ServiceRegister />} />
      <Route path="/services/:slug" element={<ServiceProvider />} />
      {/* Explore · Marketplace. /post above /:slug for the same reason. */}
      <Route path="/marketplace" element={<Marketplace />} />
      <Route path="/marketplace/post" element={<MarketplacePost />} />
      <Route path="/marketplace/:slug" element={<MarketplaceItem />} />
      {/* §12: one door for posting anything. */}
      <Route path="/post" element={<PostSomething />} />
      <Route path="/services/signup" element={<ProviderSignup />} />
      {/* Somebody's own Explore listings. RequireAuth sends a provider_user
          here; a landlord reaches it from /post. */}
      <Route path="/my-listings" element={<RequireAuth><MyListings /></RequireAuth>} />
      {/* Property adverts posted by the public (156): the landlord's advert
          screens, inside the poster's own area. Paid and approved before
          anything is shown. */}
      <Route path="/my-listings/homes/new" element={<PosterFrame><ListingForm /></PosterFrame>} />
      <Route path="/my-listings/homes/bundles" element={<PosterFrame><ListingBundles /></PosterFrame>} />
      <Route path="/my-listings/homes/:id" element={<PosterFrame><ListingForm /></PosterFrame>} />
      <Route path="/my-listings/homes/:id/promote" element={<PosterFrame><ListingPromote /></PosterFrame>} />
      {/* Sale adverts by the public: the same screens a landlord sells from.
          Any account may sell, but these lived only inside the landlord app,
          which a public account is sent away from. */}
      <Route path="/my-listings/sales" element={<PosterFrame><MySaleListings /></PosterFrame>} />
      <Route path="/my-listings/sales/new" element={<PosterFrame><SaleListingForm /></PosterFrame>} />
      <Route path="/my-listings/sales/:id" element={<PosterFrame><SaleListingForm /></PosterFrame>} />
      <Route path="/my-listings/sales/:id/pay" element={<PosterFrame><SaleListingPay /></PosterFrame>} />
      <Route path="/my-listings/sales/:id/verification" element={<PosterFrame><SaleVerification /></PosterFrame>} />
      {/* Blog. The story form sits before :slug so it is not read as one. */}
      <Route path="/blog" element={<Blog />} />
      <Route path="/blog/share-your-story" element={<ShareStory />} />
      <Route path="/blog/:slug" element={<ArticleDetail />} />
      {/* The tenant confirmation link. Public by necessity — the person
          opening it has no account and should not need one. */}
      <Route path="/inspections/confirm/:token" element={<InspectionConfirm />} />
      {/* Legal. Public in both auth states, and linked from the footer and
          the sign-up consent checkbox — which is exactly where somebody
          reads them. */}
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/delete-account" element={<DeleteAccount />} />
      <Route path="/demo" element={<RequestDemo />} />
      {/* Custom is quoted, not self-served, and is open at any portfolio size —
          so a signed-in landlord reaches the same form from Settings. */}
      <Route path="/quote" element={<RequestQuote />} />

      {/* public, an authenticated visitor is bounced to their own app —
          which for a tenant is the portal, not the landlord dashboard. */}
      <Route path="/login" element={isAuthenticated ? <Navigate to={appHome} replace /> : <Login />} />
      {/* Enrolling a second factor. Outside every shell on purpose: a platform
          session that has not enrolled is refused by the Control Centre, so
          the page it is sent to cannot live inside the Control Centre. */}
      <Route path="/security/two-factor"
        element={isAuthenticated ? <TwoFactorSetup /> : <Navigate to="/login" replace />} />
      <Route path="/register" element={isAuthenticated ? <Navigate to={appHome} replace /> : <Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      {/* Turning an invitation into a password. Public by necessity: the person
          opening it cannot sign in yet, which is the whole point. Serves staff
          and tenant invitations alike — from here they are the same act. */}
      <Route path="/accept-invitation" element={<AcceptInvitation />} />
      {/* Public sign-out confirmation, so logging out never dead-ends on /login. */}
      <Route path="/logout" element={<LoggedOut />} />

      {/* The Control Center: its own shell, its own guard, its own URL space.
          Declared BEFORE the landlord routes because it is a separate portal
          rather than a section of the app — a landlord who reaches /admin is
          sent to their own dashboard, not shown an empty one. */}
      {/* The Control Centre: its own guard here, its own CHUNK behind the
          lazy import above. A landlord never downloads these twenty-two
          screens, which is most of what the public bundle was carrying.
          The guard stays in this table rather than inside the chunk: who may
          enter a route should be readable where the route is declared. */}
      <Route
        path="/admin/*"
        element={(
          <RequireAdmin>
            <Suspense fallback={<div className="route-loading">Loading…</div>}>
              <AdminRoutes />
            </Suspense>
          </RequireAdmin>
        )}
      />

      {/* The Tenant Portal: its own shell, its own guard, its own URL space.
          Declared BEFORE the landlord routes for the same reason the Control
          Center is — it is a separate portal rather than a section of the app.
          A tenant holds tenant_portal and nothing else, so every landlord page
          would fail for them; RequireAuth sends them here instead, and
          RequireTenant sends a landlord who wanders in back to their own. */}
      <Route
        path="/portal/*"
        element={(
          <RequireTenant>
            <Suspense fallback={<div className="route-loading">Loading…</div>}>
              <PortalRoutes />
            </Suspense>
          </RequireTenant>
        )}
      />

      {/* The Owner Portal — a fourth door, like the Tenant Portal. An owner
          holds owner_portal and nothing else, so every landlord page would
          fail for them; RequireOwner sends a landlord who wanders in back to
          their own dashboard. */}
      <Route
        path="/owner/*"
        element={(
          <RequireOwner>
            <Suspense fallback={<div className="route-loading">Loading…</div>}>
              <OwnerRoutes />
            </Suspense>
          </RequireOwner>
        )}
      />

      {/* authenticated app inside the shell */}
      {/* The landlord application: fifty-odd screens in a chunk of their own,
          mounted only when somebody is signed in. The condition is what keeps
          a mistyped public URL falling through to the 404 below instead of
          being sent to sign in — a splat that matched for everyone would have
          quietly turned every typo into a login prompt. */}
      {isAuthenticated && (
        <Route
          path="/*"
          element={(
            <RequireAuth>
              <Suspense fallback={<div className="route-loading">Loading…</div>}>
                <AppRoutes />
              </Suspense>
            </RequireAuth>
          )}
        />
      )}

      {/* An unknown address says so, and offers the way back. Silently
          redirecting to "/" hid mistyped and dead links from everyone,
          ourselves included. */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}
